import objectPath from 'object-path'
import httpClient from 'utility/httpClient.util'
import { debounceCallback } from 'utility/debounceCallback.util'
import { setDashboardApi } from 'designer/component/action/Api'
import {
  dataSeries,
  createSeriesAndLabel,
  createNameAndData,
  createNameAndDuration,
} from 'designer/component/system/menu/virtualization/data/dataSeries'
import { isNumber } from 'designer/component/action/Data'
import sampleData from './stat-data.json'

export const delayFilter = (filterType, callback, delay = 1500) => {
  if (filterType === 'delay') {
    debounceCallback(callback, [true], 'delay-filter', delay)
  }
}

const createData = (chartData, serieSource, data) => {
  if (!data) return chartData
  let dataSeries = []
  let labelSeries = []
  let labelKey = objectPath.get(chartData, 'key.seriesLabel')
  let valueKey = objectPath.get(chartData, 'key.seriesValue')
  if (Array.isArray(data)) {
    data.forEach((itemValue) => {
      if (labelKey) {
        let label = objectPath.get(itemValue, labelKey)
        if (label === null) {
          label = 'null'
        }
        labelSeries.push(label)
      }
      if (valueKey) {
        let value = objectPath.get(itemValue, valueKey)
        value = isNumber(value) ? Number(value) : value
        dataSeries.push(value)
      }
    })
  } else {
    serieSource.forEach((serieName) => {
      let value = objectPath.get(data, serieName) || 0
      dataSeries.push(value)
    })
    labelSeries = serieSource
  }
  objectPath.set(chartData, 'options.labels', labelSeries)
  objectPath.set(chartData, 'series', dataSeries)
  return chartData
}

const createDataLabel = (chartData, data) => {
  if (!data) return chartData
  objectPath.set(chartData, 'series', data)
  return chartData
}

const createNameData = (chartData, data) => {
  if (!data) return chartData
  objectPath.set(chartData, 'series', data)
  return chartData
}

const createNameXY = (chartData, data) => {
  if (!data) return chartData
  objectPath.set(chartData, 'series', data)
  return chartData
}

const createNameRange = (chartData, data) => {
  if (!data) return chartData
  objectPath.set(chartData, 'series', data)
  return chartData
}

const createNameXDuration = (chartData, data) => {
  if (!data) return chartData
  return chartData
}

const checkMin = (oldMin, newMin) => {
  return newMin < oldMin ? newMin : oldMin
}

const isFormatDate = (str) => {
  let dateFormat = [
    /^\d{4}-\d{2}-\d{2}$/,
    /^(0[1-9]|1[0-2])\/(0[1-9]|1[0-2])\/\d{4}$/,
    /^(0[1-9]|1[0-2])-(0[1-9]|[12][0-9]|3[01])-(\d{4})$/,
    /\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}.\d{3}Z/,
  ]
  let isDate = dateFormat.find((format) => format.test(str))
  return isDate
}

const createLabels = (sampleData, label, formatDateData) => {
  let labels = []
  const { formatDate } = formatDateData || {}
  sampleData.forEach((sample) => {
    let dataValue = objectPath.get(sample, label)
    if (formatDate) {
      dataValue = isFormatDate(dataValue) ? formatDate(dataValue) : dataValue
    }
    if (dataValue === null) {
      dataValue = 'null'
    }
    labels.push(dataValue)
  })
  return labels
}

const createChartData = (chartData, dataSource, sampleData, formatDateData) => {
  let { labelKey } = dataSource
  let labelOptions = objectPath.has(sampleData[0])
    ? Object.keys(sampleData[0])
    : []
  let series = []
  let labels = []
  let minValue = objectPath.get(chartData, 'properties.general.min') || 0
  let maxValue = objectPath.get(chartData, 'properties.general.max') || 100
  if (labelKey) {
    let allValue = []
    labelOptions.forEach((label) => {
      if (label === labelKey) {
        labels = createLabels(sampleData, labelKey, formatDateData)
      } else {
        let data = []
        sampleData.forEach((sample) => {
          let dataValue = Number(objectPath.get(sample, label))
          data.push(dataValue)
          allValue.push(dataValue)
        })
        series.push({
          name: label,
          data: data,
        })
      }
    })
    allValue = allValue.filter((value) => isNumber(value))
    minValue = checkMin(minValue, Math.min.apply(null, allValue))
    maxValue = Math.max.apply(null, allValue)
  }
  return { series, minValue, maxValue, labels }
}

const generateRange = (min, max, step) => {
  const result = []
  for (let i = min; i <= max; i += step) {
    result.push(i)
  }
  if (result[result.length - 1] < max) {
    result.push(result[result.length - 1] + step)
  }
  return result
}

const createStep = (num, stepBy = 5) => {
  const place = Math.pow(10, Math.floor(Math.log10(num)))
  const stepValue = (Math.ceil(num / place) * place) / stepBy
  const placeStep = Math.pow(10, Math.floor(Math.log10(stepValue)))
  return Math.ceil(stepValue / placeStep) * placeStep
}

export const createSeriesArea = (
  chartData,
  dataSource,
  sampleData,
  formatDateData
) => {
  if (!sampleData) return chartData
  let chart = createChartData(chartData, dataSource, sampleData, formatDateData)
  let { series = 100, minValue = 0, maxValue = 0, labels } = chart
  objectPath.set(chartData, 'series', series)
  objectPath.set(chartData, 'options.labels', labels)
  objectPath.set(chartData, 'options.yaxis.min', minValue)
  const stepValue = objectPath.get(chartData, 'options.step') || 0
  const step = createStep(maxValue, stepValue)
  const tickList = generateRange(minValue, maxValue, step)
  const tickLength = tickList.length
  const tickAmount = tickLength - 1 === -1 ? 0 : tickLength - 1
  objectPath.set(chartData, 'options.yaxis.max', tickList[tickAmount])
  objectPath.set(chartData, 'options.yaxis.tickAmount', tickAmount)
  objectPath.set(chartData, 'options.yaxis.labels', {
    formatter: (value) => {
      return tickList.includes(Math.round(value)) ? value : ''
    },
  })
  return chartData
}

export const createSeriesBar = (
  chartData,
  dataSource,
  sampleData,
  formatDateData
) => {
  if (!sampleData) return chartData
  let { series, minValue, maxValue, labels } = createChartData(
    chartData,
    dataSource,
    sampleData,
    formatDateData
  )
  objectPath.set(chartData, 'series', series)
  objectPath.set(chartData, 'options.labels', labels)
  objectPath.set(chartData, 'options.xaxis.min', minValue)
  objectPath.set(chartData, 'options.xaxis.max', maxValue)
  const stepValue = objectPath.get(chartData, 'options.step') || 0
  const step = createStep(maxValue, stepValue)
  const tickList = generateRange(minValue, maxValue, step)
  const tickLength = tickList.length
  const tickAmount = tickLength - 1 === -1 ? 0 : tickLength - 1
  objectPath.set(chartData, 'options.xaxis.max', tickList[tickAmount])
  objectPath.set(chartData, 'options.xaxis.tickAmount', tickAmount)
  objectPath.set(chartData, 'options.xaxis.labels', {
    formatter: (value) => {
      return tickList.includes(Math.round(value)) ? value : ''
    },
  })
  return chartData
}

export const createSeriesColumn = (
  chartData,
  dataSource,
  sampleData,
  formatDateData
) => {
  if (!sampleData) return chartData
  let { series, minValue, maxValue, labels } = createChartData(
    chartData,
    dataSource,
    sampleData,
    formatDateData
  )
  objectPath.set(chartData, 'series', series)
  objectPath.set(chartData, 'options.labels', labels)
  objectPath.set(chartData, 'options.yaxis.max', maxValue)
  objectPath.set(chartData, 'options.yaxis.min', minValue)
  const stepValue = (objectPath.get(chartData, 'options.step') || 0) + 1
  const step = createStep(maxValue, stepValue)
  const tickList = generateRange(minValue, maxValue, step)
  const tickLength = tickList.length
  const tickAmount = tickLength - 1 === -1 ? 0 : tickLength - 1
  objectPath.set(chartData, 'options.yaxis.max', tickList[tickAmount])
  objectPath.set(chartData, 'options.yaxis.tickAmount', tickAmount)
  objectPath.set(chartData, 'options.yaxis.labels', {
    formatter: (value) => {
      return tickList.includes(Math.round(value)) ? value : ''
    },
  })
  return chartData
}

export const createSeriesLineColumn = (
  chartData,
  dataSource,
  sampleData,
  formatDateData
) => {
  if (!sampleData) return chartData
  let { seriesDataType, labelKey } = dataSource
  let series = []
  let options = objectPath.get(chartData, 'options')

  let labelOptions = objectPath.has(sampleData[0])
    ? Object.keys(sampleData[0])
    : []
  let foundLabel = labelOptions.find((label) => label === labelKey)
  if (foundLabel) {
    let labels = createLabels(sampleData, labelKey, formatDateData)
    objectPath.set(options, `labels`, labels)
  }

  seriesDataType.forEach(({ name, key, type }, index) => {
    let minValue = 0
    let maxValue = 100
    let data = []
    sampleData.forEach((sample) => {
      let dataValue = Number(objectPath.get(sample, key))
      data.push(dataValue)
    })
    series.push({
      name,
      type,
      data,
    })
    let allValue = data.filter((value) => isNumber(value))
    minValue = checkMin(minValue, Math.min.apply(null, allValue))
    maxValue = Math.max.apply(null, allValue)
    objectPath.set(options, `yaxis.${index}.min`, minValue)
    const stepValue = objectPath.get(chartData, 'options.step') || 0
    const step = createStep(maxValue, stepValue)
    const tickList = generateRange(minValue, maxValue, step)
    const tickLength = tickList.length
    const tickAmount = tickLength - 1 === -1 ? 0 : tickLength - 1
    objectPath.set(options, `yaxis.${index}.max`, tickList[tickAmount])
    objectPath.set(options, `yaxis.${index}.tickAmount`, tickAmount)
    objectPath.set(options, `yaxis.${index}.labels`, {
      formatter: (value) => {
        return tickList.includes(Math.round(value)) ? value : ''
      },
    })
  })
  objectPath.set(chartData, 'options', options)
  objectPath.set(chartData, 'series', series)
  return chartData
}

export const updateChartData = (
  chartData,
  dataSource,
  responseData,
  formatDateData
) => {
  let { dataKey, serieSource, seriesType, percentKey, averageKey } = dataSource
  let data = objectPath.get(responseData, dataKey)
  if (seriesType === 'data') {
    chartData = createData(chartData, serieSource, data)
  } else if (seriesType === 'data.label') {
    chartData = createDataLabel(chartData, data)
  } else if (seriesType === 'name.data') {
    chartData = createNameData(chartData, data)
  } else if (seriesType === 'name.xy') {
    chartData = createNameXY(chartData, data)
  } else if (seriesType === 'name.range') {
    chartData = createNameRange(chartData, data)
  } else if (seriesType === 'name.x.duration') {
    chartData = createNameXDuration(chartData, data)
  } else if (seriesType === 'area' || seriesType === 'line') {
    chartData = createSeriesArea(chartData, dataSource, data, formatDateData)
  } else if (seriesType === 'column') {
    chartData = createSeriesColumn(chartData, dataSource, data, formatDateData)
  } else if (seriesType === 'bar') {
    chartData = createSeriesBar(chartData, dataSource, data, formatDateData)
  } else if (seriesType === 'lineColumn') {
    chartData = createSeriesLineColumn(
      chartData,
      dataSource,
      data,
      formatDateData
    )
  } else {
    objectPath.set(chartData, 'value', data)
  }

  if (percentKey) {
    let data = objectPath.get(responseData, percentKey)
    if ((data && Array.isArray(data)) || typeof data === 'object') {
    } else {
      objectPath.set(chartData, 'percent', data)
    }
  }
  if (averageKey) {
    let data = objectPath.get(responseData, averageKey)
    if ((data && Array.isArray(data)) || typeof data === 'object') {
    } else {
      objectPath.set(chartData, 'average', data)
    }
  }
  return chartData
}

export const filterDashboardApi = async (
  context,
  delayFilter,
  isSingleVirt = false,
  urlParams = {}
) => {
  const {
    jsonForm,
    dashboardList,
    filterDashboard,
    setDashboardList,
    dateFormat,
    timeFormat,
    formatDate,
  } = context
  if (isSingleVirt) {
    let { isVirtualization = false } = jsonForm
    if (!isVirtualization) return jsonForm
    let apiData = objectPath.get(jsonForm, 'virtApi')
    if (!apiData) return jsonForm
    let api = setDashboardApi(apiData, {
      filterValue: filterDashboard,
      urlParams,
    })
    let responseData = await httpClient.get(api).then(({ data }) => {
      let apiDataKey = objectPath.get(apiData, 'dataKey')
      let responseData = apiDataKey ? objectPath.get(data, apiDataKey) : data
      return responseData
    })
    let dataSource = objectPath.get(jsonForm, 'dataSource') || {}
    let chartData = objectPath.get(jsonForm, 'chart')
    let { apiKey } = dataSource
    if (apiData.key !== apiKey) return jsonForm

    chartData = updateChartData(chartData, dataSource, responseData, {
      dateFormat,
      timeFormat,
      formatDate,
    })
    objectPath.set(jsonForm, 'chart', chartData)
    return jsonForm
  } else {
    const dashboardFilter = jsonForm?.dashboardFilter || {}
    let apiGroup = objectPath.get(dashboardFilter, 'apiGroup')
    if (apiGroup) {
      apiGroup.forEach(async (apiData) => {
        let api = setDashboardApi(apiData, { filterValue: filterDashboard })
        let dataKey = objectPath.get(apiData, 'dataKey')
        let data = await httpClient
          .get(api)
          .then(({ data }) => data)
          .catch(() => sampleData)

        let responseData = dataKey ? objectPath.get(data, dataKey) : data
        dashboardList.map((dashboard) => {
          let { design, isVirtualization } = dashboard
          if (!isVirtualization) return dashboard
          let dataSource = objectPath.get(design, 'jsonData.dataSource') || {}
          let chartData = objectPath.get(design, 'jsonData.chart')
          let { apiKey } = dataSource
          if (apiData.key !== apiKey) return dashboard
          chartData = updateChartData(chartData, dataSource, responseData, {
            dateFormat,
            timeFormat,
            formatDate,
          })
          objectPath.set(dashboard, 'design.jsonData.chart', chartData)
          return dashboard
        })
        if (delayFilter) {
          delayFilter(apiData.key, responseData)
        }
      })
    }
    setDashboardList(dashboardList)
  }
  return jsonForm
}

export const filterDataSource = (dashboardList, filterDashboard, callback) => {
  dashboardList.map(async (dashboard) => {
    let { design, isVirtualization } = dashboard
    if (!isVirtualization) {
      return dashboard
    }
    let dataSource = objectPath.get(design, 'jsonData.dataSource') || {}
    let chartData = objectPath.get(design, 'jsonData.chart')
    let url = setDashboardApi(dataSource, filterDashboard)

    if (url) {
      let responseData = await httpClient
        .get(url)
        .then(({ data }) => data)
        .catch(() => null)
      let { dataSerie } = dataSource
      if (responseData) {
        let found = dataSeries.find(({ value }) => value === dataSerie)
        if (found?.chartKey) {
          found.chartKey.forEach((key, index) => {
            let param = found.responseKey[index]
            let value = objectPath.get(responseData, param)
            objectPath.set(chartData, key, value)
          })
        }
        if (found?.value === 'data') {
          chartData = createSeriesAndLabel(responseData, dataSource, chartData)
        } else if (found?.value === 'name.data' || found?.value === 'name.xy') {
          chartData = createNameAndData(responseData, dataSource, chartData)
        } else if (found?.value === 'name.x.duration') {
          chartData = createNameAndDuration(responseData, dataSource, chartData)
        }
      }
    }
    debounceCallback(callback, [false], 'delay-filter', 1500)
    objectPath.set(dashboard, 'design.jsonData.chart', chartData)
    return dashboard
  })
  return dashboardList
}
